import { Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    height: '100%',
    minHeight: '100vh',
  },
  sidebar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.palette.primary.main,
    paddingBottom: theme.spacing(5),
    width: theme.dimensions.sidebarWidth,
    [theme.breakpoints.down('lg')]: {
      width: theme.dimensions.sidebarWidthTablet,
    },
    [theme.breakpoints.down('sm')]: {
      width: '0px',
    },
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    position: "relative",
    overflow: "hidden",
    transition: "background 0.6s ease-in-out, background-image 0.6s ease-in-out",
    backgroundSize: 'cover',
    minHeight: '100vh',

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: `linear-gradient(45deg,
        ${theme.palette.primary.light} 0%,
        ${theme.palette.secondary.main} 50%,
        ${theme.palette.primary.dark} 100%)`,
      opacity: 0.1,
      animation: "rotate 15s infinite linear",
      zIndex: 0,
    },
    "@keyframes rotate": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" },
    },
    boxShadow: `
      0 16px 32px rgba(0, 0, 0, 0.2),
      0 0 0 1px rgba(255, 255, 255, 0.1) inset
    `,
    [theme.breakpoints.down('sm')]: {
      width: "100%",
      maxWidth: "100%",
      height: "100%",
      borderRadius: 0,
      justifyContent: "center",
      paddingTop: theme.spacing(4),
    },
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
    position: 'relative',
    zIndex: 1,
  },
}));

// Cache para evitar verificações repetidas
const backgroundCache = new Map();

const checkImage = (src) =>
  new Promise((resolve) => {
    // Verificar no cache primeiro
    if (backgroundCache.has(src)) {
      resolve(backgroundCache.get(src));
      return;
    }

    const img = new Image();
    img.onload = () => {
      backgroundCache.set(src, true);
      resolve(true);
    };
    img.onerror = () => {
      backgroundCache.set(src, false);
      resolve(false);
    };
    // Timeout para evitar espera infinita
    setTimeout(() => {
      if (!img.complete) {
        backgroundCache.set(src, false);
        resolve(false);
      }
    }, 2000);
    img.src = src;
  });

const LoginLayout = ({ children }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const revenda = useSelector((state) => state.session.revenda);

  const [backgroundAvailable, setBackgroundAvailable] = useState({
    light: false,
    dark: false,
  });

  const [isChecking, setIsChecking] = useState(false);

  const verifyBackgrounds = useCallback(async () => {
    if (isChecking || !revenda?.code) return;

    setIsChecking(true);

    try {
      let lightUrl, darkUrl;

      if (revenda?.code) {
        lightUrl = `/web/themes/${revenda.code}/imagens/light-background.webp`;
        darkUrl = `/web/themes/${revenda.code}/imagens/dark-background.webp`;
      } else {
        // Fallback para imagens padrão (que provavelmente não existem)
        lightUrl = '/imagens/light-background.webp';
        darkUrl = '/imagens/dark-background.webp';
      }

      const [lightOk, darkOk] = await Promise.all([
        checkImage(lightUrl),
        checkImage(darkUrl),
      ]);

      setBackgroundAvailable({ light: lightOk, dark: darkOk });
    } catch (error) {
      console.warn('Erro ao verificar backgrounds:', error);
      setBackgroundAvailable({ light: false, dark: false });
    } finally {
      setIsChecking(false);
    }
  }, [revenda?.code, isChecking]);

  useEffect(() => {
    verifyBackgrounds();
  }, [verifyBackgrounds]);

  const lightPattern = `linear-gradient(135deg,
    rgba(230, 240, 255, 0.9) 0%,
    rgba(208, 225, 255, 0.9) 25%,
    rgba(255, 255, 255, 0.9) 50%,
    rgba(230, 240, 255, 0.9) 100%
  )`;

  const darkPattern = `linear-gradient(135deg,
    rgba(10, 25, 47, 0.9) 0%,
    rgba(13, 31, 58, 0.9) 25%,
    rgba(18, 43, 74, 0.9) 50%,
    rgba(10, 25, 47, 0.9) 100%
  )`;

  const getBackgroundStyle = () => {
    const isDarkMode = theme.palette.mode === 'dark';

    // Se temos uma revenda com código e a imagem existe, usar imagem
    if (revenda?.code) {
      if (isDarkMode && backgroundAvailable.dark) {
        return {
          backgroundImage: `url(/web/themes/${revenda.code}/imagens/dark-background.webp)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
      } else if (!isDarkMode && backgroundAvailable.light) {
        return {
          backgroundImage: `url(/web/themes/${revenda.code}/imagens/light-background.webp)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
      }
    }

    // Fallback para gradiente (nunca falha)
    return {
      backgroundImage: isDarkMode ? darkPattern : lightPattern,
      backgroundSize: '400% 400%',
      animation: 'gradientShift 20s ease infinite',
    };
  };

  return (
    <main className={classes.root}>
      <Paper className={classes.paper} style={getBackgroundStyle()}>
        <form className={classes.form}>
          {children}
        </form>
      </Paper>
    </main>
  );
};

export default LoginLayout;